const finite=(value,fallback)=>{
  const parsed=Number(value);
  return Number.isFinite(parsed)?parsed:fallback;
};

const clamp=(value,minimum,maximum,fallback=minimum)=>(
  Math.max(minimum,Math.min(maximum,finite(value,fallback)))
);

export function inspectAudioLoopEdges(buffer,maxTrimSeconds=.25){
  const sampleRate=Math.max(1,Number(buffer?.sampleRate)||44100);
  const length=Math.max(0,Number(buffer?.length)||0);
  const channels=Math.max(0,Number(buffer?.numberOfChannels)||0);
  if(!length||!channels){
    return {suggestedStartTrim:0,suggestedEndTrim:0};
  }
  let peak=0;
  for(let channel=0;channel<channels;channel+=1){
    const data=buffer.getChannelData(channel);
    for(let index=0;index<data.length;index+=1){
      peak=Math.max(peak,Math.abs(data[index]));
    }
  }
  if(peak<.00001)return {suggestedStartTrim:0,suggestedEndTrim:0};
  const threshold=Math.max(.0005,peak*.012);
  const limit=Math.min(length-1,Math.ceil(sampleRate*maxTrimSeconds));
  let first=0;
  let last=length-1;
  for(let index=0;index<=limit;index+=1){
    let audible=false;
    for(let channel=0;channel<channels;channel+=1){
      if(Math.abs(buffer.getChannelData(channel)[index])>=threshold){
        audible=true;
        break;
      }
    }
    if(audible){
      first=index;
      break;
    }
  }
  for(let offset=0;offset<=limit;offset+=1){
    const index=length-1-offset;
    let audible=false;
    for(let channel=0;channel<channels;channel+=1){
      if(Math.abs(buffer.getChannelData(channel)[index])>=threshold){
        audible=true;
        break;
      }
    }
    if(audible){
      last=index;
      break;
    }
  }
  const safetySeconds=.0015;
  return {
    suggestedStartTrim:Number(Math.max(0,first/sampleRate-safetySeconds).toFixed(3)),
    suggestedEndTrim:Number(Math.max(0,(length-1-last)/sampleRate-safetySeconds).toFixed(3)),
  };
}

export function createSeamlessAudioLoop(context,sourceBuffer,settings={}){
  if(!context?.createBuffer||!sourceBuffer?.getChannelData)return sourceBuffer;
  const sampleRate=Math.max(1,Number(sourceBuffer.sampleRate)||44100);
  const sourceLength=Math.max(1,Number(sourceBuffer.length)||1);
  const startTrim=clamp(settings.sampleLoopStart,0,.25,0);
  const endTrim=clamp(settings.sampleLoopEndTrim,0,.25,0);
  const requestedCrossfade=clamp(settings.sampleLoopCrossfade,0,.15,.04);
  const startFrame=Math.min(sourceLength-1,Math.round(startTrim*sampleRate));
  const endFrame=Math.max(startFrame+1,sourceLength-Math.round(endTrim*sampleRate));
  const rangeLength=endFrame-startFrame;
  const crossfadeFrames=Math.min(
    Math.round(requestedCrossfade*sampleRate),
    Math.floor((rangeLength-1)/3),
  );
  if(startFrame===0&&endFrame===sourceLength&&crossfadeFrames<2)return sourceBuffer;
  const outputLength=Math.max(1,rangeLength-Math.max(0,crossfadeFrames));
  const output=context.createBuffer(
    sourceBuffer.numberOfChannels,
    outputLength,
    sampleRate,
  );
  for(let channel=0;channel<sourceBuffer.numberOfChannels;channel+=1){
    const input=sourceBuffer.getChannelData(channel);
    const result=output.getChannelData(channel);
    if(crossfadeFrames<2){
      result.set(input.subarray(startFrame,endFrame));
      continue;
    }
    const coreStart=startFrame+crossfadeFrames;
    const coreEnd=endFrame-crossfadeFrames;
    const coreLength=Math.max(0,coreEnd-coreStart);
    if(coreLength)result.set(input.subarray(coreStart,coreEnd),0);
    for(let index=0;index<crossfadeFrames;index+=1){
      const progress=index/(crossfadeFrames-1);
      const fadeOut=Math.cos(progress*Math.PI*.5)**2;
      const fadeIn=Math.sin(progress*Math.PI*.5)**2;
      result[coreLength+index]=(
        input[endFrame-crossfadeFrames+index]*fadeOut
        +input[startFrame+index]*fadeIn
      );
    }
  }
  return output;
}
